import { addMinutes, isBefore } from 'date-fns';
import type { Box, Shipment, ShipmentState, TemperatureMeasurement } from 'graphql-models';
import { boxFactory } from './box';
import { clientFactory } from './client';
import { consigneeFactory } from './consignee';
import { randomId, randomUuid, type Factory } from './helpers';
import { locationMeasurementFactory, temperatureMeasurementFactory } from './measurements';
import { pictureFactory } from './picture';
import { sourceSystemFactory } from './source-system';

const MOCK_SHIPMENT: Shipment = {
  id: '1',
  barcodeExternal: '123456789',
  updatedAt: new Date('Mon Mar 21 2025 15:15:35 GMT+0100').toISOString(),
  expectedDeliveryDate: new Date('Mon Mar 24 2025 18:00:00 GMT+0100').toISOString(),
  requestedDeliveryDate: new Date('Mon Mar 24 2025 15:11:35 GMT+0100').toISOString(),
  minTemperature: 3.3,
  maxTemperature: 9,
  weightKg: 122.12,
  alerts: [],
  pictures: [pictureFactory({ id: '1' }), pictureFactory({ id: '2' })],
  temperatureMeasurements: [
    temperatureMeasurementFactory({
      valueCelsius: 3.6,
      measuredAt: new Date('Mon Mar 21 2025 15:11:35 GMT+0100').toISOString(),
    }),
    temperatureMeasurementFactory({
      valueCelsius: 5.1,
      measuredAt: new Date('Mon Mar 21 2025 15:12:35 GMT+0100').toISOString(),
    }),
    temperatureMeasurementFactory({
      valueCelsius: 6.7,
      measuredAt: new Date('Mon Mar 21 2025 15:13:35 GMT+0100').toISOString(),
    }),
    temperatureMeasurementFactory({
      valueCelsius: 7.2,
      measuredAt: new Date('Mon Mar 21 2025 15:14:35 GMT+0100').toISOString(),
    }),
    temperatureMeasurementFactory({
      valueCelsius: 6.1,
      measuredAt: new Date('Mon Mar 21 2025 15:15:35 GMT+0100').toISOString(),
    }),
    temperatureMeasurementFactory({
      valueCelsius: 5.1,
      measuredAt: new Date('Mon Mar 21 2025 15:16:35 GMT+0100').toISOString(),
    }),
  ],
  currentTemperature: temperatureMeasurementFactory({
    valueCelsius: 5.1,
    measuredAt: new Date('Mon Mar 21 2025 15:16:35 GMT+0100').toISOString(),
  }),
  consignee: consigneeFactory({
    id: '1',
  }),
  locationMeasurements: [
    locationMeasurementFactory({ longitude: -1.057169, latitude: 45.916157 }),
    locationMeasurementFactory({ longitude: 1.86856, latitude: 46.16914 }),
    locationMeasurementFactory({ longitude: 4.835659, latitude: 45.764042 }),
    locationMeasurementFactory({ longitude: 5.04148, latitude: 47.322048 }),
    locationMeasurementFactory({ longitude: 2.354314, latitude: 48.857705 }),
  ],
  state: 'DELIVERED',
  parcelId: 'SHIP202503242098',
  parcelSequenceNumber: 1,
  parcelTotal: 3,
  pin: '111111',
  client: clientFactory(),
  externalUuid: '01992de6-db05-730e-a448-0f6c266ae16e',
  createdAt: new Date('Mon Mar 21 2024 15:16:35 GMT+0100').toISOString(),
  shipmentStates: [],
  sourceSystem: sourceSystemFactory(),
};

export const shipmentFactory: Factory<Shipment & { forBox: (box?: Partial<Box>) => Shipment }> = (
  overrides,
) => ({
  ...MOCK_SHIPMENT,
  id: randomId(),
  externalUuid: randomUuid(),
  ...overrides,
  forBox: (box) => ({
    ...MOCK_SHIPMENT,
    id: randomId(),
    ...overrides,
    box: boxFactory(box),
  }),
});

export const shipmentStateFactory: Factory<ShipmentState> = (overrides) => ({
  state: 'ORDER_RECEIVED',
  changedAt: new Date().toISOString(),
  estimatedArrivalTime: new Date().toISOString(),
  id: randomId(),
  ...overrides,
});

export const temperatureSeries = (
  startDate: Date,
  start: number = 5,
  min: number = 2,
  max: number = 8,
  breach?: number,
) => {
  const result: TemperatureMeasurement[] = [];
  let currentDate = new Date(startDate);
  const today = new Date();

  while (isBefore(currentDate, today)) {
    let randomValue = start + Math.random() - 0.5;
    randomValue = Math.max(min, Math.min(max, randomValue)); // Clamp value within min and max

    result.push(
      temperatureMeasurementFactory({
        valueCelsius: randomValue,
        measuredAt: new Date(currentDate).toISOString(),
      }),
    );

    currentDate = addMinutes(currentDate, 1);
  }

  // If a breach value is provided and the array has values, set it as last value
  if (breach !== undefined && result.length > 0) {
    const lastIndex = result.length - 1;
    if (result[lastIndex]) {
      result[lastIndex].valueCelsius = breach;
    }
  }

  return result;
};
